'use client';

/**
 * usePaymentData Hook
 *
 * Manages payment data state for the purchase flow.
 * Fetches payment methods, options, and purchase details from API.
 *
 * @example
 * ```tsx
 * const {
 *   loading,
 *   error,
 *   paymentMethods,
 *   selectedPaymentMethod,
 *   fetchData
 * } = usePaymentData();
 *
 * // Fetch data on mount
 * useEffect(() => {
 *   fetchData(10.00, 0, { isPayg: false });
 * }, []);
 * ```
 */

import { useState, useCallback } from 'react';
import { fetchCombinedPaymentSources } from '../api';
import { withCaptchaPromise } from '../utils/withCaptchaPromise';
import {
  extractCurrentPaymentMethod,
  mapPaymentMethodFromApi,
  mapPaymentOptionsFromApi,
  ensurePaymentOptionsWithIap,
  getIapDefaultIfNoMethods,
  transformToPurchaseDetails
} from '../utils/dataTransformers';
import type {
  PaymentMethod, PaymentOption, PurchaseDetails, WithCaptchaFn, SupportedCurrency
} from '../types';
import type { CombinedPaymentSourcesResponse } from '../api';

/** Options for usePaymentData hook */
export interface UsePaymentDataOptions {
  withCaptcha: WithCaptchaFn;
  /** When provided, used for IAP support check (purchase-type filtering); else falls back to URL. */
  purchaseType?: string;
  /** Called when purchase details are loaded from the API (e.g. to sync fees into analytics config). */
  onPurchaseDetailsLoaded?: (details: PurchaseDetails) => void;
  /** Called when payment options are set from the API (e.g. to sync availableMethods into analytics). */
  onPaymentOptionsLoaded?: (options: PaymentOption[]) => void;
  /** Initial exchange rate — passed to /combined; internal rate is updated from fees.fx_rate response */
  fxRate: number;
  /** Initial ISO currency key — passed to /combined; resolved currency is updated from fees.currency */
  currencyKey: string;
  /** Full list of supported currencies for resolving the confirmed currency object */
  supportedCurrencies: SupportedCurrency[];
}

/** Return type for usePaymentData hook */
interface UsePaymentDataResult {
  /** Whether data is being fetched */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Currently selected payment method */
  selectedPaymentMethod: PaymentMethod | null;
  /** All available payment methods */
  paymentMethods: PaymentMethod[];
  /** Available payment options (for adding new methods) */
  paymentOptions: PaymentOption[];
  /** Purchase details including fees */
  purchaseDetails: PurchaseDetails | null;
  /**
   * Extra params keyed by payment kind, sourced from the /combined `current` array's
   * `adding: true` items. Used to forward e.g. `force_add_kind=pp` to process_credits
   * when the user has no saved method and adds one during checkout.
   */
  addingExtraParams: Record<string, string>;
  /**
   * Server-confirmed exchange rate (fees.fx_rate from /combined), or the initial
   * fxRate prop while the first fetch is in flight. Used for all /process_credits calls.
   */
  fxRate: number;
  /**
   * Server-confirmed currency object (resolved from fees.currency via supportedCurrencies),
   * or the initial resolved currency while the first fetch is in flight.
   */
  currency: SupportedCurrency;
  /** Fetch payment data from API */
  fetchData: (price: number, bonus: number, isPayg?: boolean, creditsRequested?: number) => Promise<void>;
  /** Update selected payment method */
  setSelectedPaymentMethod: (method: PaymentMethod | null) => void;
  /** Update payment methods list and selection */
  updatePaymentMethods: (methods: PaymentMethod[], selectedMethod: PaymentMethod | null) => void;
  /** Update available payment options */
  setPaymentOptions: (options: PaymentOption[]) => void;
  /** Reset all data to initial state */
  resetData: () => void;
}

export const usePaymentData = ({
  withCaptcha,
  purchaseType,
  onPurchaseDetailsLoaded,
  onPaymentOptionsLoaded,
  fxRate: initialFxRate,
  currencyKey,
  supportedCurrencies
}: UsePaymentDataOptions): UsePaymentDataResult => {
  const resolveCurrency = (key: string) => supportedCurrencies.find((c) => c.key === key)!;

  // Start true so all platforms show skeleton from first paint until data loads (avoids empty flash / measure race)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
  const [purchaseDetails, setPurchaseDetails] = useState<PurchaseDetails | null>(null);
  const [addingExtraParams, setAddingExtraParams] = useState<Record<string, string>>({});
  // fxRate and currency start as prop-derived values; updated to server-confirmed values after /combined
  const [fxRate, setFxRate] = useState<number>(initialFxRate);
  const [currency, setCurrency] = useState<SupportedCurrency>(() => resolveCurrency(currencyKey));

  const fetchData = useCallback(async (price: number, bonus: number, isPayg = false, creditsRequested?: number) => {
    setLoading(true);
    setError(null);

    try {
      // Pass the initial prop values to /combined — the response confirms the authoritative values
      const response: CombinedPaymentSourcesResponse = await withCaptchaPromise(
        'checkout',
        (rcToken: string) => fetchCombinedPaymentSources(price, isPayg, rcToken, currencyKey, initialFxRate, creditsRequested),
        withCaptcha
      );

      const currentPaymentMethod = extractCurrentPaymentMethod(response);
      const methods = (response.payment_sources?.existing || []).map(mapPaymentMethodFromApi);
      const selected = getIapDefaultIfNoMethods(methods, currentPaymentMethod, purchaseType);
      const options = ensurePaymentOptionsWithIap(
        mapPaymentOptionsFromApi(response.payment_sources?.available),
        purchaseType
      );
      const details = transformToPurchaseDetails(response, price, bonus);

      const extraParams: Record<string, string> = {};
      (response.current || []).forEach((item) => {
        if (item.adding && item.extraParams) {
          extraParams[item.kind] = item.extraParams;
        }
      });

      setSelectedPaymentMethod(selected);
      setPaymentMethods(methods);
      setPaymentOptions(options);
      setPurchaseDetails(details);
      setAddingExtraParams(extraParams);

      // Update fxRate and currency from the server-confirmed values in the fees response
      if (response.fees?.fx_rate !== undefined) setFxRate(response.fees.fx_rate);
      if (response.fees?.currency) setCurrency(resolveCurrency(response.fees.currency));

      onPurchaseDetailsLoaded?.(details);
      onPaymentOptionsLoaded?.(options);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load payment data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [withCaptcha, purchaseType, currencyKey, initialFxRate, onPurchaseDetailsLoaded, onPaymentOptionsLoaded]);

  const setSelectedPaymentMethodHandler = useCallback((method: PaymentMethod | null) => {
    setSelectedPaymentMethod(method);
  }, []);

  const updatePaymentMethods = useCallback((
    methods: PaymentMethod[],
    selectedMethod: PaymentMethod | null
  ) => {
    setPaymentMethods(methods);
    setSelectedPaymentMethod(selectedMethod);
  }, []);

  const setPaymentOptionsHandler = useCallback((options: PaymentOption[]) => {
    setPaymentOptions(options);
  }, []);

  const resetData = useCallback(() => {
    setSelectedPaymentMethod(null);
    setPaymentMethods([]);
    setPaymentOptions([]);
    setPurchaseDetails(null);
    setError(null);
  }, []);

  return {
    loading,
    error,
    selectedPaymentMethod,
    paymentMethods,
    paymentOptions,
    purchaseDetails,
    addingExtraParams,
    fxRate,
    currency,
    fetchData,
    setSelectedPaymentMethod: setSelectedPaymentMethodHandler,
    updatePaymentMethods,
    setPaymentOptions: setPaymentOptionsHandler,
    resetData
  };
};
